<!--
확장형 input 요소의 기본템플릿
------------------------------------
사용 예 )
------------------------------------
하위 컴포넌트 속성전달 case
    <app-input-ex
        v-model="columns.p_brand"
        id="aaa"
        name="bbb"
        placeholder="ccc"
        input-size="large"
        :validate-rules="{required: true, title: 'title'}"
        init-error-msg="에러내용"
    > </app-input-ex>
------------------------------------
라벨 출력
    <app-input-ex
        label="라벨"
    > </app-input-ex>
------------------------------------
필수값 설정
    <app-input-ex
        label="라벨"
        required
        required-text="required"
        required-layout="label-left"
    > </app-input-ex>
    > </app-input-ex>
------------------------------------
필수값 설정
    <app-input-ex
        label="라벨"
        desc-msg="설명입니다"
    > </app-input-ex>


------------------------------------
라디오,체크박스 (옵션명부분 html태그 커스텀처리)
    data 정의부분
       옵션배열정의할때, desc라는 속성을 추가로 정의해서 사용함.
        labelUseTypeOptions: [
            {text: '사용', val: 1, desc: '(업체가 첨부한 리턴라벨을 사용하여 리턴처리 함)'},
            {text: '미사용', val: 0, desc: '(업체로부터 리턴라벨이 첨부되지 않아 현지택배(UPS등) 사용하여 리턴처리 함)'},
        ],

   템플릿 정의부분
        <app-input-ex
            class="uk-margin"
            type="radio"
            v-model="labelUseType"
            label="리턴라벨 사용여부"
            init-error-msg=""
            :options="labelUseTypeOptions"
        >
            <template
                slot="optionText"
                slot-scope="{ option }"
                class=""
            >
                <span class="uk-text-bold">{{ option.text }}</span>
                <span>{{ option.desc }}</span>

            </template>
        </app-input-ex>



-->
<template>
    <div class="uk-clearfix" :style="[defaultCssVariables, cssVariables]">
        <label
            class="uk-form-label"
            :class="labelClassObject"
            @click="inputFocus"
            v-if="label"
        >
            <!-- 필수값 라벨자체를 강조해서 표시 -->
            <div
                v-if="highlighted"
                class="uk-display-inline-block label-highlight"
            >
                <span class="required-text" v-if="required">{{ requiredText }}</span>

                <slot name="label">
                    {{ label }}
                </slot>
            </div>

            <!-- 필수값 라벨 좌측에 작게표시 -->
            <div v-if="required && requiredLayout === 'label-left'" class="uk-display-inline-block">
                <span class="uk-text-meta">{{ requiredText }}</span>
                <slot name="label">
                    {{ label }}
                </slot>
            </div>


            <!-- 필수 : 우측에 작게표시 -->
            <div v-if="required && requiredLayout === 'label-right-sup'" class="uk-display-inline-block">
                <slot name="label">
                    {{ label }}
                </slot>
                <sup class="uk-text-muted">{{ requiredText }}</sup>
            </div>



            <!-- 필수, 하이라이트 아닌 경우 -->
            <div v-if="(! required) && (! highlighted)" class="uk-display-inline-block">
                <slot name="label">
                    {{ label }}
                </slot>
            </div>


            <!-- 검증상태 -->
            <span v-show="validateState === 'success'" class="validate-success-sign" :uk-icon="'icon: check; ratio: ' + validateSuccessSignRatio"></span>
        </label>

        <div class="uk-form-controls">
            <component
                :is="componentName"
                ref="input"

                :id="id"
                :name="name"
                :placeholder="placeholder"
                :css-variables="inputCssVariables"
                :multiselect-class="multiselectClass"

                :text="text"

                :value="value"
                @update:value="updateValue"
                :input-size="inputSize"
                :validate-rules="validateRules"
                :options=" options"
                :init-error-msg="initErrorMsg"
                :textarea-rows="textareaRows"
                :readonly="readonly"
                :disabled="disabled"

                @update:state="updateState"
                @update:is-focus="updateIsFocus"
            >
                <!-- 라디오,체크박스 에서 사용하는 옵션명 커스텀정의 슬롯 -->
                <template
                    slot="optionText"
                    slot-scope="{ option }"
                >
                    <slot
                        name="optionText"
                        :option="option"
                    >
                        {{ option.text }}
                    </slot>
                </template>

                <!-- 커스텀 placeholder 정의 슬롯 -->
                <template
                    slot="customPlaceholder"
                >
                    <slot
                        name="customPlaceholder"
                    >
                    </slot>
                </template>
            </component>



            <!-- 필수 -->
            <sup v-if="required && requiredLayout === 'input-bottom'" class="uk-text-muted">{{ requiredText }}</sup>
        </div>

        <!-- 에러메세지 -->
        <div v-show="errorMsg" class="error-msg">
            {{ errorMsg }}
        </div>

        <!-- 설명메세지 -->
        <div v-show="descMsg" class="desc-msg">
            {{ descMsg }}
        </div>
    </div>
</template>

<script>
    import ExBase from './base.vue';

    export default {
        extends: ExBase,

        computed: {
            /**
             * 디폴트 css 변수정의
             */
            defaultCssVariables: function () {
                return _.assignIn(ExBase.computed.defaultCssVariables(), {
                    '--label-highlight-bg-color': '#ffff99'
                });
            },

            /**
             * input요소에 전달할 css 변수정의
             */
            inputCssVariables: function () {
                return {
                    '--bg-color': this.highlighted ? '#ffff99' : '#fff'
                };
            },

            /**
             * 하이라이트 적용여부
             * @returns boolean
             */
            highlighted() {
                return this.highlight
                    || (this.required && this.requiredLayout === 'label-highlight');
            },


            /**
             * 정의된 타입별 컴포넌트 네임을 반환
             */
            componentName: function () {
                return 'app-input-' + this.type;
            },

            /**
             * 검증성공 체크표시의 사이즈 (ratio 속성)
             */
            validateSuccessSignRatio: function () {
                if (this.inputSize === 'small') {
                    return '0.8';
                } else if (this.inputSize === 'large') {
                    return '1.2';
                }
                return '1';
            },
            
        }
    }
</script>


<style lang="scss" scoped>
// 검증성공 체크표시
.validate-success-sign {
    color: #009a67;
}

// 라벨
.uk-form-label{
    // 사이즈
    &.label-small {
        font-size: 1rem;
    }
    &.label-large {
        font-size: 1.125rem;
    }
    // 포커스
    &.label-focus {
        color: #1e87f0;
    }
}
.uk-form-horizontal .uk-form-label{
    // 사이즈
    &.label-small {
        margin-top: 3px
    }
    &.label-large {
        margin-top: 10px
    }
}

// 오류메세지
.error-msg{
    color: #f0506e ;
    font-size: 0.75rem;
    text-align: right;
}

// 설명
.desc-msg{
    color: #999;
    font-size: 0.75rem;
    text-align: right;
}

.label-highlight {
    //--bg-color: #ffff99;
    padding: 0.25rem;
    //font-size: 1rem;
    color: #000 !important;
    background-color: var(--label-highlight-bg-color);
    font-weight: bold;
    span.required-text {
        //font-size: 0.875rem;
        //line-height: 1.4;
        //color: #999;
    }
}
</style>